import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, MapPin, Phone, Mail } from "lucide-react";
import { STORE } from "../lib/constants";

const NAV = [
  { label: "Home", path: "/", testid: "nav-link-home" },
  { label: "About Our Shop", path: "/about", testid: "nav-link-about" },
  { label: "Crow Chronicles", path: "/blog", testid: "nav-link-blog" },
  { label: "Contact & Visit", path: "/contact", testid: "nav-link-contact" },
];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-[#FDFBF7]">
      <ScrollToTop />
      <div className="bg-[#111110] py-1.5 text-center text-[11px] font-mono uppercase tracking-[0.25em] text-[#E8DFC8]">
        Primitive &amp; country goods · dressed for Christmas · online ordering coming soon
      </div>
      <header className="sticky top-0 z-50 w-full border-b border-[#D8CDBC] bg-[#EBE1CB]/90 backdrop-blur-md">
        <div className="crow-container flex h-24 items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img
              src="/logo-horizontal.jpeg"
              alt="The Olde Crow Boutique logo"
              className="h-16 w-auto rounded-md border-2 border-[#292725] bg-[#F4EFE6] px-1 shadow-md sm:h-24"
            />
            <span className="hidden font-hand text-lg leading-tight text-[#8A7E70] sm:block">
              country · primitive ·<br />cozy home &amp; gifts
            </span>
          </Link>
          <nav className="hidden items-center gap-6 lg:flex" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors hover:text-[#C27803] ${
                    isActive ? "text-[#8C251C]" : "text-[#292725]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-3 lg:flex">
            <Link
              to="/contact"
              data-testid="nav-enquire-button"
              className="rounded-md bg-[#C27803] px-4 py-2 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#9A5E02]"
            >
              Enquire / Visit
            </Link>
            <Link
              to="/admin/login"
              data-testid="nav-admin-link"
              className="rounded-md border border-[#292725] px-3 py-2 font-mono text-xs text-[#292725] transition-colors hover:bg-[#292725] hover:text-[#FDFBF7]"
            >
              Admin
            </Link>
          </div>
          <button
            data-testid="mobile-menu-button"
            className="rounded-md border border-[#D8CDBC] p-2 text-[#292725] lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {open && (
          <nav className="border-t border-[#D8CDBC] bg-[#FDFBF7] px-4 py-4 lg:hidden" data-testid="mobile-nav">
            <div className="flex flex-col gap-3">
              {NAV.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-${item.testid}`}
                  className="rounded-md px-2 py-2 text-sm font-medium text-[#292725] hover:bg-[#F4EFE6]"
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/admin/login"
                data-testid="mobile-nav-admin-link"
                className="rounded-md border border-[#292725] px-3 py-2 text-center font-mono text-xs text-[#292725]"
              >
                Admin Portal
              </Link>
            </div>
          </nav>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="grain-overlay mt-0 bg-[#111110] text-[#E8DFC8]" data-testid="site-footer">
        <div className="crow-container grid gap-10 py-16 md:grid-cols-3">
          <div>
            <img src="/logo-horizontal.jpeg" alt="The Olde Crow Boutique logo" className="h-20 w-auto rounded-md border border-[#3A3632]" />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-[#B9AE9C]">
              Country &amp; primitive home decor and gifts — carefully gathered and curated by the owner, in keeping with the country cozy.
            </p>
            <p className="mt-4 max-w-xs rounded-md border border-[#3A3632] bg-[#1A1918] px-3 py-2 text-xs leading-relaxed text-[#B9AE9C]" data-testid="footer-sister-store">
              <span className="font-semibold text-[#E8873A]">The Kilted Goat Country Store</span> has blended into The Olde Crow — one shop now, same owner, at Sunnyside Mall.
            </p>
            <p className="mt-4 font-hand text-xl text-[#C27803]">"Gather &amp; be thankful."</p>
          </div>
          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-[#8A7E70]">Wander the Shop</h3>
            <ul className="mt-4 space-y-2 text-sm">
              {NAV.map((item) => (
                <li key={item.path}>
                  <Link to={item.path} className="text-[#E8DFC8] transition-colors hover:text-[#C27803]">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-[#8A7E70]">Visit Us</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#C27803]" />
                <span>{STORE.address}</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-[#C27803]" />
                <span>{STORE.phone}</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-[#C27803]" />
                <span>{STORE.email}</span>
              </li>
            </ul>
            <p className="mt-4 text-xs text-[#8A7E70]">Mon–Fri 10–7 · Sat 10–6 · Sun 12–5</p>
          </div>
        </div>
        <div className="border-t border-[#3A3632] py-5 text-center font-mono text-xs text-[#8A7E70]">
          © {new Date().getFullYear()} The Olde Crow Boutique · Est. 2018 · Bedford, Nova Scotia · Gathered with heart in the country
        </div>
      </footer>
    </div>
  );
}
